import axios from "axios";
import { authService } from "./authService";

const env = process.env.REACT_APP_ENVIRONMENT;
const apiBaseUrl =
  env === "LOCAL"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PRODUCTION;

const apiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: { "Content-Type": "application/json" },
  timeout: 90000,
});

apiClient.interceptors.request.use(async (config) => {
  const token = await authService.acquireToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const DATE_FIELDS = new Set(["agreement_date", "renewal_date"]);

// ContractView (nested) → flat UI object used by contract components
const mapContractViewToUI = ({ id, created_at, contract }) => ({
  id,
  created_at,
  name: contract.name ?? "",
  team_member: contract.employee_name ?? "",
  employee_id: contract.employee_id ?? null,
  agreement_date: contract.agreement_date ?? "",
  renewal_date: contract.renewal_date ?? "",
  status: contract.status ?? "",
  contract_file: contract.contract_file ?? "",
});

// Flat UI object → ContractEntry payload expected by the backend
const mapUIToPayload = (flat) => {
  const payload = {};
  const fields = [
    "name",
    "employee_id",
    "agreement_date",
    "renewal_date",
    "status",
    "contract_file",
  ];
  for (const key of fields) {
    const value = flat[key];
    if (DATE_FIELDS.has(key)) {
      payload[key] = value === "" || value == null ? null : value;
    } else {
      payload[key] = value === "" || value == null ? null : value;
    }
  }
  return payload;
};

class ContractService {
  /** List contracts with optional search, status filter, and pagination. */
  getContracts = async (search = "", limit = 50, skip = 0, filters = {}) => {
    const params = new URLSearchParams({
      limit: String(limit),
      skip: String(skip),
    });
    if (search) params.append("search", search);
    if (filters.status?.length) {
      filters.status.forEach((s) => params.append("status", s));
    }
    if (filters.employee_id != null) {
      params.append("employee_id", String(filters.employee_id));
    }
    const { data } = await apiClient.get(`/api/contracts?${params}`);
    return {
      ...data,
      items: data.items.map(mapContractViewToUI),
    };
  };

  /** Fetch a single contract by id. Returns flat UI object. */
  getContract = async (id) => {
    const { data } = await apiClient.get(`/api/contracts/${id}`);
    return mapContractViewToUI(data);
  };

  /** Create a contract. payload = flat UI fields object. */
  createContract = async (payload) => {
    try {
      const { data } = await apiClient.post(
        "/api/contracts",
        mapUIToPayload(payload),
      );
      return mapContractViewToUI(data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create contract";
      throw new Error(msg);
    }
  };

  /** Update a contract. payload = flat UI fields object. */
  updateContract = async (id, payload) => {
    try {
      const { data } = await apiClient.put(
        `/api/contracts/${id}`,
        mapUIToPayload(payload),
      );
      return mapContractViewToUI(data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to update contract";
      throw new Error(msg);
    }
  };

  /** Upload a contract file. Returns { file_name, stored_name, path }. */
  uploadContractFile = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiClient.post("/api/contracts/files/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  /** Build a direct URL for a stored contract file path (for <a href> / Open link). */
  getContractFileUrl = (path) => {
    const params = new URLSearchParams({ path });
    return `${apiBaseUrl}/api/contracts/files/file?${params}`;
  };

  /** Fetch a contract file as a Blob for in-page preview. Returns { blob, contentType }. */
  getContractFile = async (path) => {
    const res = await apiClient.get("/api/contracts/files/file", {
      params: { path },
      responseType: "blob",
      validateStatus: (status) => status >= 200 && status < 300,
    });
    return {
      blob: res.data,
      contentType: res.headers["content-type"] || "application/octet-stream",
    };
  };

  /** Delete a contract by id. */
  deleteContract = async (id) => {
    const res = await apiClient.delete(`/api/contracts/${id}`);
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  /** Export contracts to an Excel blob. */
  exportContracts = async ({ search, status, employee_id } = {}) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status?.length) status.forEach((s) => params.append("status", s));
    if (employee_id != null) params.set("employee_id", String(employee_id));

    const res = await apiClient.get(`/api/contracts/export?${params}`, {
      responseType: "blob",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      validateStatus: (s) => s >= 200 && s < 300,
    });
    return res.data; // Blob
  };
}

export const contractService = new ContractService();
